import React, { useState, useEffect } from 'react'
import { useQuery } from '@tanstack/react-query'
import api from '../../utils/api'

const JobSelector = ({ onJobSelect, selectedJobId }) => {
  const [dateFilter, setDateFilter] = useState('30') // days

  const { data: jobsResponse, isLoading, error } = useQuery({
    queryKey: ['user-jobs-completed', dateFilter],
    queryFn: async () => {
      const params = {
        page: 1,
        per_page: 50,
        status: 'completed' // Only show completed jobs
      }

      const response = await api.get('/user/jobs', { params })
      return response.data
    }
  })

  const jobs = jobsResponse?.jobs || []

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const getStatusBadge = (status) => {
    const statusColors = {
      completed: 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400',
      failed: 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400',
      processing: 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400',
      pending: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400'
    }

    return (
      <span className={`px-2 py-1 text-xs font-medium rounded-full ${statusColors[status] || statusColors.pending}`}>
        {status}
      </span>
    )
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className="ml-2 text-gray-600 dark:text-gray-400">Loading jobs...</span>
      </div>
    )
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <div className="text-red-500 mb-2">⚠️ Error loading jobs</div>
        <p className="text-gray-600 dark:text-gray-400">{error.message}</p>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {/* Date Filter */}
      <div className="max-w-xs">
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Date Range
        </label>
        <select
          value={dateFilter}
          onChange={(e) => setDateFilter(e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
        >
          <option value="7">Last 7 days</option>
          <option value="30">Last 30 days</option>
          <option value="90">Last 90 days</option>
          <option value="365">Last year</option>
        </select>
      </div>

      {/* Jobs List */}
      {jobs && jobs.length > 0 ? (
        <div className="space-y-3 max-h-96 overflow-y-auto" data-testid="job-list">
          {jobs.map((job) => (
            <div
              key={job.id}
              data-testid={`job-item-${job.id}`}
              onClick={() => onJobSelect(job)}
              className={`p-4 border rounded-lg cursor-pointer transition-colors ${
                selectedJobId === job.id
                  ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 dark:border-blue-400'
                  : 'border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500 hover:bg-gray-50 dark:hover:bg-gray-700'
              }`}
            >
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h4 className="font-medium text-gray-900 dark:text-white">
                      {job.original_filename || job.title || `Job ${job.id}`}
                    </h4>
                    {getStatusBadge(job.status)}
                  </div>

                  <div className="text-sm text-gray-600 dark:text-gray-400 space-y-1">
                    <div className="flex items-center gap-4">
                      <span>ID: {job.id}</span>
                      <span>Source: {job.source_lang || 'N/A'}</span>
                      <span>Target: {Array.isArray(job.target_langs) ? job.target_langs.join(', ') : (job.target_langs || 'N/A')}</span>
                      {job.word_count && <span>Words: {job.word_count.toLocaleString()}</span>}
                    </div>

                    <div className="flex items-center gap-4">
                      <span>Created: {formatDate(job.created_at)}</span>
                      {job.completed_at && (
                        <span>Completed: {formatDate(job.completed_at)}</span>
                      )}
                    </div>
                  </div>
                </div>

                {selectedJobId === job.id && (
                  <div className="text-blue-600 dark:text-blue-400 ml-4">
                    ✓ Selected
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-12" data-testid="empty-state">
          <div className="w-12 h-12 bg-gray-400 rounded mx-auto mb-4"></div>
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
            No jobs found
          </h3>
          <p className="text-gray-600 dark:text-gray-400">
            No jobs match your current filters. Try adjusting the status or date range.
          </p>
        </div>
      )}
    </div>
  )
}

export default JobSelector